<script>
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { account, appSession, appConstants } from "../stores/app.js";
  import { ScrollingDisable, ScrollingEnable } from "../library/util.js";
  import RestActions from "../components/RestActions.svelte";
  import RestJumpseatRequests from "../components/RestJumpseatRequests.svelte";
  import MyModal from "../components/MyModal.svelte";
  import { DateTime } from "luxon";
  import JSRequestFormatter from "../components/JSRequestFormatter.svelte";

  export let jumpseatRequestId;
  const dispatch = createEventDispatcher();
  let busy = false;
  let myModal = null;
  let restActions = null;
  let restJumpseatRequests = null;
  let request = null;

  const dressStandard = {
    uniform: "Uniform",
    business: "Business",
    businesscasual: "Business-casual",
  };

  onMount(async () => {
    ScrollingDisable();

    try {
      busy = true;

      // load jumpseat request
      request = await restJumpseatRequests.GetJumpseatRequest(jumpseatRequestId);

      request.emptyleg.datetime = DateTime.fromSQL(request.emptyleg.datetime);
      request.emptyleg.datetimelocal = DateTime.fromSQL(request.emptyleg.datetimelocal);
      const gmtDiff = request.emptyleg.datetimelocal.diff(request.emptyleg.datetime, "hours");
      gmtDiff.toObject();
      request.emptyleg.gmtoffset = gmtDiff.as("hours") < 0 ? gmtDiff.as("hours") : "+" + gmtDiff.as("hours");
      request.operator.jumpseat_dressstandard = dressStandard[request.operator.jumpseat_dressstandard];

      let required = [];
      if (request.operator.jumpseat_kcmrequired == 1) required.push("KCM");
      if (request.operator.jumpseat_cassrequired == 1) required.push("CASS");
      request.operator.required = required.length > 0 ? required.join(", ") + " required" : null;

      if (request.operator.jumpseat_conditions)
        request.operator.jumpseat_conditions = request.operator.jumpseat_conditions
          .replace(/\r\n/g, "<br />")
          .replace(/[\r\n]/g, "<br />");

      busy = false;
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, operatorId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          myModal.showAlert("Error", error);
          break;
      }
    }
  });

  onDestroy(() => {
    ScrollingEnable();
  });

  async function cancelRequest() {
    try {
      let confirmed = await myModal.showConfirm("Confirm cancellation", "Cancel this jumpseat request?", {
        buttonOk: "Yes",
        buttonCancel: "No",
      });
      if (!confirmed) return;

      busy = true;

      await restActions.JumpseatRequestCancel(request.jumpseatrequestid);

      busy = false;

      // close modal and parent
      dispatch("close", { changed: { requests: true } });
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, operatorId: null });
          break;
        case "NetworkError":
          await myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          await myModal.showAlert("Error", error);
          break;
      }
    }
  }

  async function Done() {
    dispatch("close", { changed: { requests: false } });
  }
</script>

<style>
  .section-header {
    font-size: 1em;
    margin-top: 10px;
    margin-bottom: 0px;
    color: #000;
    font-weight: 500;
  }
</style>

<div class="overlay-page-wrapper" transition:fly={{ y: 200, duration: 500 }}>
  <div class="overlay-page-header valign-wrapper">
    <div class="container">

      <h5 class="center-align">Jumpseat Request</h5>

    </div>
  </div>

  <div class="overlay-page-body">
    <div class="container">

      {#if request}
        <JSRequestFormatter status={request.status} emptyLeg={request.emptyleg} />

        {#if request.status == 'approved'}
          <div style="font-size: 0.8em;">
            {request.emptyleg.aircraft.model}
            Tail {request.emptyleg.aircraft.tail}
          </div>
        {/if}

        {#if request.status == 'approved' || request.status == 'pending'}
          <div style="text-align: center;margin-top: 20px; margin-bottom: 20px;">
            <button class="waves-effect waves-light btn-small blue-grey" on:click={cancelRequest}>
              Cancel Request
            </button>
          </div>
        {/if}

        {#if request.status == 'approved'}
          {#if request.flightdetails}
            <div class="section-header">DETAILS / INSTRUCTIONS</div>
            <div style="font-size: 0.85em; line-height: 1.25em;">{request.flightdetails}</div>
          {/if}

          <div class="section-header">OPERATOR</div>
          <div style="font-size: 0.85em;">
            {request.operator.name}
            <br />
            {request.operator.phone}
            <br />
            {#if request.operator.website}
              {request.operator.website}
              <br />
            {/if}
          </div>

          <div class="section-header">CONDITIONS</div>

          <div style="font-size: 0.85em;">

            {#if request.operator.required}
              <div style="margin-bottom: 4px;">{request.operator.required}</div>
            {/if}

            {#if request.operator.jumpseat_dressstandard}
              <div style="margin-bottom: 10px;">Dress standard: {request.operator.jumpseat_dressstandard}</div>
            {/if}

            {#if request.operator.jumpseat_conditions}
              {@html request.operator.jumpseat_conditions}
            {/if}
          </div>
        {/if}
      {/if}

    </div>
  </div>

  <div class="overlay-page-footer">
    <div class="container">

      {#if busy}
        <div class="preloader-wrapper small active">
          <div class="spinner-layer spinner-green-only">
            <div class="circle-clipper left">
              <div class="circle" />
            </div>
            <div class="gap-patch">
              <div class="circle" />
            </div>
            <div class="circle-clipper right">
              <div class="circle" />
            </div>
          </div>
        </div>
      {/if}

      <div class="right">
        <button class="btn-flat waves-effect" on:click={Done}>Done</button>
      </div>

    </div>
  </div>

</div>

<MyModal bind:this={myModal} />
<RestActions bind:this={restActions} />
<RestJumpseatRequests bind:this={restJumpseatRequests} />
